<av-content-container [fullHeight]="true">
  <div class="av-all-demands d-flex flex-column h-100 w-100 px-4 overflow-hidden">
    <mat-tab-group
      class="av-all-demands__tabs flex-shrink-0 mb-3"
      [selectedIndex]="activeTabIndex()"
      (selectedTabChange)="onSubjectTabChange($event)"
    >
      @for (tab of facade.permittedTabs(); track tab) {
        <mat-tab [label]="tabLabelKey(tab) | translate"></mat-tab>
      }
    </mat-tab-group>

    <section class="av-all-demands__toolbar flex-shrink-0 d-flex flex-column">
      <div class="av-all-demands__filters-row d-flex align-items-center gap-2 w-100 flex-wrap">
        <mat-form-field
          appearance="outline"
          class="av-all-demands__search"
          subscriptSizing="dynamic"
        >
          <mat-icon matPrefix>search</mat-icon>
          <input
            matInput
            type="text"
            [formControl]="searchControl"
            [maxlength]="searchMaxLength"
            [placeholder]="'app.reports.allDemands.search_placeholder' | translate"
            autocomplete="off"
          />
          @if (searchControl.value.trim().length > 0) {
            <button
              type="button"
              matSuffix
              mat-icon-button
              class="av-all-demands__search-clear"
              [attr.aria-label]="'shared.general.buttons.close' | translate"
              (click)="onClearSearch()"
            >
              <mat-icon>close</mat-icon>
            </button>
          }
        </mat-form-field>

        <button
          type="button"
          mat-icon-button
          class="av-all-demands__reload flex-shrink-0"
          [disabled]="facade.loading()"
          [avPending]="refreshWait.visible()"
          [attr.aria-label]="'app.catalog.applications.reload' | translate"
          [matTooltip]="'shared.general.tooltips.refresh' | translate"
          (click)="facade.reload()"
        >
          @if (refreshWait.visible()) {
            <av-spin size="sm" />
          } @else {
            <mat-icon>refresh</mat-icon>
          }
        </button>

        <button
          type="button"
          mat-stroked-button
          class="flex-shrink-0"
          [disabled]="facade.exportBusy()"
          [avPending]="facade.exportPhase() === 'starting'"
          (click)="onStartExport()"
        >
          @if (facade.exportPhase() === 'starting') {
            <av-spin size="sm" />
          } @else {
            <mat-icon fontSet="material-symbols-outlined">file_export</mat-icon>
          }
          {{ 'app.reports.allDemands.action.export' | translate }}
        </button>

        @if (facade.activeTab(); as activeTab) {
          <app-all-demands-filter-panel
            [tab]="activeTab"
            [criteria]="facade.criteria()"
            [activeFilterCount]="facade.activeFilterCount()"
            [hasFilters]="facade.hasFilters()"
            [optionsState]="facade.filterOptionsState()"
            [requesterOptions]="facade.requesterOptions()"
            [subjectOptions]="facade.subjectOptions()"
            [approverOptions]="facade.approverOptions()"
            [moderatorOptions]="facade.moderatorOptions()"
            (criteriaChange)="onCriteriaChange($event)"
            (clearAll)="onClearFilters()"
            (opened)="facade.requestFilterOptions()"
          />
        }
      </div>
    </section>

    @if (facade.exportPhase() !== 'idle') {
      <app-report-export-status
        class="av-all-demands__export flex-shrink-0"
        [phase]="facade.exportPhase()"
        [rowCount]="facade.exportRowCount()"
        [failureKey]="facade.exportFailureKey()"
        [downloading]="facade.exportDownloading()"
        (download)="facade.downloadExport()"
        (dismiss)="facade.dismissExport()"
      />
    }

    @if (showSkeleton()) {
      <div
        class="av-all-demands__skeleton flex-grow-1 d-flex flex-column"
        [attr.aria-busy]="facade.loading()"
      >
        <span class="visually-hidden" aria-live="polite">
          @if (coldWait.visible()) {
            {{ 'app.reports.allDemands.loading' | translate }}
          }
        </span>
        @if (coldWait.visible()) {
          <div class="table-header py-2">
            <av-skeleton class="av-all-demands__skeleton-title" shape="text" [rows]="1" />
            <av-skeleton shape="count" />
          </div>

          <av-skeleton
            class="av-all-demands__skeleton-table"
            shape="table"
            [rows]="facade.filters().pageSize"
            [widths]="skeletonColumnWidths"
            [withPaginator]="true"
          />
        }
      </div>
    } @else if (facade.hasError()) {
      <div class="flex-grow-1 d-flex flex-column justify-content-center">
        <mat-card appearance="outlined" class="av-all-demands__error p-4 text-center">
          <mat-icon class="av-all-demands__error-icon mb-2">error</mat-icon>
          <p class="m-0 mb-3">{{ 'shared.api.errors.default' | translate }}</p>
          <button mat-flat-button color="primary" type="button" (click)="facade.reload()">
            <mat-icon>refresh</mat-icon>
            {{ 'shared.general.tooltips.refresh' | translate }}
          </button>
        </mat-card>
      </div>
    } @else if (showEmpty()) {
      <div class="flex-grow-1 d-flex flex-column justify-content-center">
        <av-empty-state
          [icon]="emptyIsFiltered() ? 'search_off' : 'inbox'"
          [titleKey]="
            emptyIsFiltered()
              ? 'app.reports.allDemands.empty.filtered'
              : 'app.reports.allDemands.empty.none'
          "
        >
          @if (emptyIsFiltered()) {
            <button mat-stroked-button type="button" (click)="onClearAllCriteria()">
              <mat-icon>filter_alt_off</mat-icon>
              {{ 'app.reports.allDemands.empty.clearFilters' | translate }}
            </button>
          }
        </av-empty-state>
      </div>
    } @else {
      <div class="table-header py-2">
        <div class="header-content">
          <h2 class="table-title">{{ 'app.reports.allDemands.title' | translate }}</h2>
          <p class="table-description m-0">
            {{ 'app.reports.allDemands.description' | translate }}
          </p>
        </div>
        @if (facade.totalCount()) {
          <div class="count-line">
            <span class="count-badge">{{ facade.totalCount() }}</span>
            <span class="count-label">{{ 'app.reports.allDemands.countSuffix' | translate }}</span>
          </div>
        }
      </div>

      <div class="table-wrapper" [attr.aria-busy]="facade.loading()">
        <av-rail [active]="refreshWait.visible()" [offset]="'var(--avyna-table-header-height)'" />

        <table
          mat-table
          matSort
          [dataSource]="rows()"
          [trackBy]="trackRow"
          matSortDisableClear
          [matSortActive]="facade.filters().orderBy"
          [matSortDirection]="facade.filters().order"
          (matSortChange)="onSortChange($event)"
        >
          <ng-container matColumnDef="subject">
            <th mat-header-cell *matHeaderCellDef scope="col" mat-sort-header="subject">
              {{ 'app.reports.allDemands.columns.subject' | translate }}
            </th>
            <td mat-cell *matCellDef="let row">
              <a
                class="av-subject-cell d-inline-flex align-items-center gap-2 text-decoration-none"
                [routerLink]="row.subjectLink"
              >
                @if (row.isApplication && row.hasIcon) {
                  <span
                    class="av-subject-cell__icon d-inline-flex align-items-center justify-content-center"
                    [style.background]="row.gradient"
                  >
                    <img
                      avAppIcon
                      class="av-subject-cell__img"
                      [src]="row.subjectIconUrl"
                      [alt]="row.subjectName"
                    />
                  </span>
                } @else if (row.isApplication) {
                  <span
                    class="av-subject-cell__icon d-inline-flex align-items-center justify-content-center"
                  >
                    <span
                      class="av-subject-cell__img av-app-icon-placeholder"
                      aria-hidden="true"
                    ></span>
                  </span>
                } @else {
                  <av-avatar
                    [name]="row.subjectName || ('shared.general.labels.na' | translate)"
                    size="sm"
                  ></av-avatar>
                }
                <span class="d-inline-flex flex-column">
                  <span
                    class="av-subject-cell__name text-truncate d-inline-flex align-items-center gap-1"
                  >
                    @if (row.subjectName) {
                      {{ row.subjectName }}
                    } @else {
                      {{ row.subjectFallbackKey! | translate: row.subjectFallbackParams }}
                    }
                    @if (row.deleted && row.subjectName) {
                      <span class="av-deleted-badge">{{
                        'shared.general.labels.deleted' | translate
                      }}</span>
                    }
                  </span>
                  <span class="av-subject-cell__subtitle text-muted small">
                    {{ row.subjectTypeKey | translate }}
                  </span>
                </span>
              </a>
            </td>
          </ng-container>

          <ng-container matColumnDef="reference">
            <th mat-header-cell *matHeaderCellDef scope="col" mat-sort-header="reference">
              {{ 'app.reports.allDemands.columns.reference' | translate }}
            </th>
            <td mat-cell *matCellDef="let row">
              <av-reference [reference]="row.reference" />
            </td>
          </ng-container>

          <ng-container matColumnDef="requester">
            <th mat-header-cell *matHeaderCellDef scope="col" mat-sort-header="requester">
              {{ 'app.reports.allDemands.columns.requester' | translate }}
            </th>
            <td mat-cell *matCellDef="let row">
              @if (row.requesterName) {
                <span class="d-inline-flex align-items-center gap-2">
                  <av-avatar [name]="row.requesterName" size="sm"></av-avatar>
                  <span class="av-requester-cell d-inline-flex flex-column">
                    <span class="av-requester-cell__name text-truncate">{{
                      row.requesterName
                    }}</span>
                    @if (row.requesterEmail) {
                      <span class="av-requester-cell__email text-muted small text-truncate">{{
                        row.requesterEmail
                      }}</span>
                    }
                  </span>
                </span>
              } @else {
                <span class="text-muted">{{ 'shared.general.labels.na' | translate }}</span>
              }
            </td>
          </ng-container>

          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef scope="col" mat-sort-header="status">
              {{ 'app.reports.allDemands.columns.status' | translate }}
            </th>
            <td mat-cell *matCellDef="let row">
              <app-demand-status-chip [status]="row.status"></app-demand-status-chip>
            </td>
          </ng-container>

          <ng-container matColumnDef="progress">
            <th mat-header-cell *matHeaderCellDef scope="col" mat-sort-header="progress">
              {{ 'app.reports.allDemands.columns.progress' | translate }}
            </th>
            <td mat-cell *matCellDef="let row">
              @if (row.isActive && row.currentApprovalStep && row.currentApprovalTotal) {
                <span class="d-inline-flex flex-column">
                  <span class="av-progress-cell__step">
                    {{
                      'app.reports.allDemands.progress.stepOf'
                        | translate
                          : { step: row.currentApprovalStep, total: row.currentApprovalTotal }
                    }}
                  </span>
                  @if (row.currentApprovalGroupName) {
                    <span
                      class="av-progress-cell__stage text-muted small d-inline-flex align-items-center gap-1"
                    >
                      <span class="text-truncate">{{ row.currentApprovalGroupName }}</span>
                      <span>{{ 'app.catalog.approvalSteps.groupSuffix' | translate }}</span>
                      <button
                        type="button"
                        class="av-progress-cell__info"
                        [attr.aria-label]="'app.catalog.approvalSteps.groupTooltipAria' | translate"
                        [matTooltip]="
                          'app.catalog.approvalSteps.groupTooltip'
                            | translate: { group: row.currentApprovalGroupName }
                        "
                        matTooltipPosition="above"
                      >
                        <mat-icon class="av-progress-cell__info-icon">info</mat-icon>
                      </button>
                    </span>
                  } @else if (row.currentApprovalApproverName) {
                    <span class="av-progress-cell__stage text-muted small text-truncate">
                      {{ row.currentApprovalApproverName }}
                    </span>
                  } @else if (row.currentApprovalTypeKey) {
                    <span class="av-progress-cell__stage text-muted small">
                      {{ row.currentApprovalTypeKey | translate }}
                    </span>
                  }
                </span>
              } @else {
                <span class="text-muted">{{ row.closureKey | translate }}</span>
              }
            </td>
          </ng-container>

          <ng-container matColumnDef="requested">
            <th mat-header-cell *matHeaderCellDef scope="col" mat-sort-header="created_at">
              {{ 'app.reports.allDemands.columns.requested' | translate }}
            </th>
            <td mat-cell *matCellDef="let row">
              <span [matTooltip]="row.createdAt | date: 'dd/MM/yyyy HH:mm'">
                {{ row.createdAt | date: 'dd/MM/yyyy' }}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="lastUpdate">
            <th mat-header-cell *matHeaderCellDef scope="col" mat-sort-header="updated_at">
              {{ 'app.reports.allDemands.columns.lastUpdate' | translate }}
            </th>
            <td mat-cell *matCellDef="let row">
              <span [matTooltip]="row.updatedAt | date: 'dd/MM/yyyy HH:mm'">
                {{ row.updatedAt | date: 'dd/MM/yyyy' }}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef scope="col" class="text-end">
              {{ 'app.reports.allDemands.columns.actions' | translate }}
            </th>
            <td mat-cell *matCellDef="let row" class="text-end">
              <a
                mat-stroked-button
                [routerLink]="row.subjectLink"
                [attr.aria-label]="'app.reports.allDemands.action.view' | translate"
              >
                <mat-icon>visibility</mat-icon>
                {{ 'app.reports.allDemands.action.view' | translate }}
              </a>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
        </table>
      </div>

      <mat-paginator
        class="pagination-bar"
        [length]="facade.totalCount()"
        [pageSize]="facade.filters().pageSize"
        [pageIndex]="facade.filters().page"
        [pageSizeOptions]="[facade.filters().pageSize]"
        [hidePageSize]="true"
        [showFirstLastButtons]="true"
        (page)="onPage($event)"
      ></mat-paginator>
    }
  </div>
</av-content-container>
